import * as STD from '@emersya/mpa-std';
import * as CM from '@emersya/mpa-components-manager';
import {
	InputSelect,
	Replicator,
	InputNumber,
	InputButton
} from '@emersya/mpa-components';

export interface IViewerMenuButtonConfiguration {
	action: 'default' | 'edit' | 'rotate' | 'duplicate' | 'delete';
	generator?: object;
};

export interface IViewerMenuComponentConfiguration extends CM.DOMComponent.IConfiguration {
	readonly mode: 'fixed' | 'contextual';
	isContextual?: boolean;
	generator: CM.PropertyOperation.TDescriptor;
	buttons: Record<string, IViewerMenuButtonConfiguration>;
};

export class ViewerMenuComponent extends CM.DOMComponent<
	HTMLDivElement,
	ViewerMenuComponent,
	IViewerMenuComponentConfiguration
> {
	static currentMenu: ViewerMenuComponent | null = null;

	private static async onDocumentClick(event: MouseEvent) {
		const cm = this.currentMenu;
		if (!cm || !cm.isMenuClosingEnabled) {
			return;
		}
		const replicatorComponent = <Replicator.Component>cm.component_find('@:edit.content.replicator');
		const clickedNode = <Node>event.target;
		if (
			!cm.component_DOM.contains(clickedNode) &&
			(
				!replicatorComponent.component_isDisabled ?
					!replicatorComponent.component_DOM.contains(clickedNode) &&
					!replicatorComponent.target?.component_DOM.contains(clickedNode) :
					true
			) &&
			(
				document.documentElement.style.pointerEvents !== 'none' &&
				clickedNode !== document.documentElement
			)
		) {
			await cm.disableCustomImages();
			await cm.component_parent.unselectAll();
		}
	};

	static override initialize() {
		CM.DOMComponent.initialize.call(this);
		document.addEventListener('click', this.onDocumentClick.bind(this));
	};

	component_DOM: HTMLDivElement;
	isOpen = false;
	declare component_parent: ViewerComponent;

	override async component_start(ev: STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_start(ev);
		await this.component_disable();
	};

	override async component_enable() {
		ViewerMenuComponent.currentMenu = this;
		await super.component_enable();
		this.updatePosition();
		this.isOpen = true;
	};

	override async component_disable() {
		if (ViewerMenuComponent.currentMenu === this) {
			ViewerMenuComponent.currentMenu = null;
		}
		await super.component_disable();
		this.isOpen = false;
	};

	private async menu_open(event: STD.events.Event<any, CM.Component<any, any>>) {
		this.component_DOM.style.display = 'none';
		await this.component_enable();
		await this.updateWithCurrentProduct();
		this.component_DOM.style.display = '';
		if (
			(event.details.mouseButton === 'right') &&
			event.details.meshesNames?.length
		) {
			const currentInstance = this.getCurrentInstance();
			const hasMenu = Object.values(currentInstance.product.category.menuActions).some(x => x);
			if (!hasMenu) {
				return;
			}
			const customImages = currentInstance.product.extension?.customImages || [];
			const customImage = (<any[]>customImages).find(x => event.details.meshesNames.includes(x.mesh));
			if (customImage) {
				await this.changeBlock('edit');
				const graphicsComponent = this.component_find('@:edit.content.graphics');
				if (!graphicsComponent) {
					this.component_throwLibraryError('Graphics component not found!', {});
				}
				const idx = customImages.indexOf(customImage);
				const ci = <ViewerGraphicsComponent>graphicsComponent.component_outerChilds[idx];
				for (const _ci of <ViewerGraphicsComponent[]>graphicsComponent.component_outerChilds) {
					if (ci !== _ci) {
						_ci.component_DOM.classList.add('notHighlighted');
					}
				}
				ci.component_DOM.scrollIntoView({
					behavior: 'smooth',
					block: 'start'
				});
			}
		}
	};

	private async menu_close() {
		await this.component_disable();
	};

	private async onProductInstanceSelection(event: STD.events.Event<any, CM.Component<any, any>>) {
		const {isEnabled} = <ViewerComponent.ISelectionEventDetails>event.details;
		if (!isEnabled || this.component_parent.currentSelectedInstance === undefined) {
			await this.menu_close();
		} else {
			await this.menu_open(event);
		}
	};

	private onCameraUpdated() {
		const productInstance = app.viewer.currentSelectedInstance;
		if (this.component_isDisabled || !productInstance) {
			return;
		}
		this.updatePosition();
	};

	private async action_edit() {
		await this.changeBlock('edit');
		await this.updateReplicator();
	};

	private action_rotate() {
		const productInstance = app.viewer.currentSelectedInstance;
		if (this.component_isDisabled || !productInstance) {
			return;
		}
		const oldRotation = productInstance.position.scene.rotation.y;
		let newRotation = oldRotation + 45;
		newRotation = newRotation - (newRotation % 45);
		productInstance.updatePosition({
			rotation: {y: newRotation}
		});
	};

	private async action_duplicate() {
		await <any>void 1;
		// const productInstance = this.planner.planner.currentSelectedInstance;
		// if (this.component_isDisabled || !productInstance) {
		// 	return;
		// }
		// const position = <[ number, number, number ]> [
		// 	productInstance.position.scene.x,
		// 	productInstance.position.scene.y,
		// 	productInstance.position.scene.z
		// ];
		// position[ 0 ] += .5;
		// position[ 1 ] += 0;
		// position[ 2 ] += 0;
		// await productInstance.product.clone(
		// 	productInstance,
		// 	position,
		// 	1
		// );
		// await this.component_disable();
	};

	private async action_delete() {
		const productInstance = app.viewer.currentSelectedInstance;
		if (this.component_isDisabled || !productInstance) {
			return;
		}
		await productInstance.delete();
		await this.component_disable();
	};

	private async action_toggle_lock() {
		const productInstance = app.viewer.currentSelectedInstance;

		if (this.component_isDisabled || !productInstance)
			return;

		const lockButton = <InputButton.Component>this.component_find('@:edit.content.lock');
		const unlockButton = <InputButton.Component>this.component_find('@:edit.content.unlock');

		const isLocked = !productInstance.isLocked;

		if (isLocked) {
			await lockButton.component_disable();
			await unlockButton.component_enable();
		} else {
			await lockButton.component_enable();
			await unlockButton.component_disable();
		}

		await productInstance.setLocked(isLocked);
	}

	private async disableCustomImages() {
		const graphicsComponent = this.component_find('@:edit.content.graphics');
		if (!graphicsComponent) {
			this.component_throwLibraryError('Graphics component not found!', {});
		}
		await graphicsComponent.component_disable();
		this.updatePosition();
	};

	private async enableCustomImages() {
		const graphicsComponent = this.component_find('@:edit.content.graphics');
		if (!graphicsComponent) {
			this.component_throwLibraryError('Graphics component not found!', {});
		}
		const currentInstance = app.viewer.currentSelectedInstance;
		if (!currentInstance) {
			this.component_throwUserError('No instance currently selected!', {});
		}
		const extension = currentInstance.product.extension;
		if (!extension) {
			this.component_throwUserError('enableCustomImages called with a product without customImages!', {
				currentInstance
			});
		}
		const maxCustomImages = graphicsComponent.component_outerChilds.length;
		if (extension.customImages) {
			const usedCustomImagesCount = extension.customImages.length;
			for (let i = 0; i < maxCustomImages; i++) {
				const ci = <ViewerGraphicsComponent>graphicsComponent.component_outerChilds[i];
				// const ciExtension = extension.customImages[ i ];
				if (i < usedCustomImagesCount) {
					// if (ciExtension && ciExtension.pricing && currentInstance.basketPart) {
					// 	const [ productId, ...part ] = currentInstance.basketPart;
					// 	if (!productId) {
					// 		this.component_throwLibraryError('');
					// 	}
					// 	const _price : MPAApplication.Basket.IPrice = <any> (ciExtension.pricing.price[ '*' ] || {})[ 1 ] ||
					// 		{ exclTax : 0, inclTax : 0 };
					// 	if (!(<any> _price).exclTax) {
					// 		(<any> _price).exclTax = 0;
					// 	}
					// 	if (!(<any> _price).inclTax) {
					// 		(<any> _price).inclTax = 0;
					// 	}
					// 	if (!(_price.exclTax instanceof STD.math.BigNumber)) {
					// 		_price.exclTax = new STD.math.BigNumber((<any> _price).exclTax);
					// 	}
					// 	if (!(_price.inclTax instanceof STD.math.BigNumber)) {
					// 		_price.inclTax = new STD.math.BigNumber((<any> _price).inclTax);
					// 	}
					// 	const price = MPAApplication.Basket.Price.from(
					// 		_price,
					// 		(ciExtension.pricing.taxes.map(x =>
					// 				new MPAApplication.Basket.Tax(x.id, new STD.math.BigNumber(x.value), x.type))
					// 		) || []
					// 	);
					// 	ci.component_configuration.basketInfos = {
					// 		part,
					// 		price :                          price,
					// 		product :                        productId,
					// 		id :                             ciExtension.mesh,
					// 		useSurfaceAsVolume :             ciExtension.pricing.useSurfaceAsVolume,
					// 		taxes :                          ciExtension.pricing.taxes,
					// 		priceComputationByVolumeMethod : ciExtension.pricing.priceComputationByVolumeMethod
					// 	};
					// } else {
					// 	delete ci.component_configuration.basketInfos;
					// }
					const graphicsValue = (<any>currentInstance.metadata.customImages)[i];
					if (graphicsValue.showEditor === false) {
						await ci.component_disable();
					} else {
						await ci.component_enable();
						await ci.updateValue(graphicsValue);
					}
				} else {
				}
			}
			await graphicsComponent.component_enable();
		}
		this.updatePosition();
	};

	onGraphicEnabled() {
		if (!this.isOpen) {
			return;
		}
		this.updatePosition();
	};

	private previousInstance: ViewerComponent.ProductInstance | null = null;

	async updateReplicator() {
		await <any>void 1;
		const currentInstance = app.viewer.currentSelectedInstance;
		if (!currentInstance) {
			this.component_throwUserError('No instance currently selected!', {});
		}
		const updateWithCurrentProductEvent = this.component_getEventWhichRepresentNoEvent();
		updateWithCurrentProductEvent.details.product = currentInstance;
		const replicatorComponent = <Replicator.Component>this.component_find('@:edit.content.replicator');

		let component: CM.DOMComponent<any, any, any>;
		const extension = currentInstance.product.extension;
		if (!extension || !extension.configuratorGet) {
			return;
		}
		await replicatorComponent.component_enable();
		if (extension.configuratorPrepare) {
			const prepareFunction = await extension.configuratorPrepare.execute(this, updateWithCurrentProductEvent);
			await prepareFunction(currentInstance);
		}
		// const getConfiguratorComponentFunction = await extension.configuratorGet.execute(
		// 	this,
		// 	updateWithCurrentProductEvent
		// );
		// const componentAccessor = await getConfiguratorComponentFunction(currentInstance);


		const componentAccessor = await extension.configuratorGet.execute(
			this,
			updateWithCurrentProductEvent
		);
		// const componentAccessor = await getConfiguratorComponentFunction(currentInstance);
		if (componentAccessor instanceof CM.DOMComponent) {
			component = componentAccessor;
		} else {
			component = <any>this.component_find(componentAccessor);
		}
		await replicatorComponent.updateTarget(component, true);
		// if (extension.configuratorClean) {
		// 	const cleanFunction = await extension.configuratorClean.get(this, updateWithCurrentProductEvent);
		// 	await cleanFunction(currentInstance);
		// }
	};

	private async updateWithCurrentProduct() {
		const currentInstance = app.viewer.currentSelectedInstance;
		if (!currentInstance) {
			this.component_throwUserError('No instance currently selected!', {});
		}
		const updateWithCurrentProductEvent = this.component_getEventWhichRepresentNoEvent();
		updateWithCurrentProductEvent.details.product = currentInstance;
		const editComponent = <CM.DOMComponent<any, any, any>>this.component_find('@:edit');
		const editTitleComponent = <CM.DOMComponent<any, any, any>>editComponent.component_find('@header.title');
		const editDescriptionComponent = <CM.DOMComponent<any, any, any>>editComponent.component_find('@content.description');
		const editOptionsComponent = <CM.DOMComponent<any, any, any>>editComponent.component_find('@content.options');
		const editOptionsSelectComponent = <InputSelect.Component>editOptionsComponent.component_find('@select');
		for (const c of Array.from(editOptionsSelectComponent.component_outerChilds)) { // copy because delete make iterator livings
			await c.component_delete();
		}
		if (editOptionsSelectComponent.value) {
			editOptionsSelectComponent.value = null;
		}
		const extension = currentInstance.product.extension;
		let hasDescription = false;
		let hasPresets = false;
		let hasCustomImages = false;
		const replicatorComponent = <Replicator.Component>this.component_find('@:edit.content.replicator');
		// =========================================================================
		// =========================================================================
		// =========================================================================
		// =========================================================================
		// =========================================================================
		if (extension) {
			const baseTrId = 'plannerProduct.' + currentInstance.product.getId();
			if (extension.description) {
				hasDescription = true;
				editDescriptionComponent.component_DOM.dataset.translation = baseTrId + '.description';
			}
			if (extension.configuratorGet) {
				await replicatorComponent.component_enable();
				let component: CM.DOMComponent<any, any, any>;
				if (extension.configuratorPrepare) {
					const prepareFunction = await extension.configuratorPrepare.execute(this, updateWithCurrentProductEvent);
					await prepareFunction(currentInstance);
				}
				const componentAccessor = await extension.configuratorGet.execute(
					this,
					updateWithCurrentProductEvent
				);
				if (componentAccessor instanceof CM.DOMComponent) {
					component = componentAccessor;
				} else {
					component = <any>this.component_find(componentAccessor);
				}
				await replicatorComponent.updateTarget(component, true);
				// if (extension.configuratorClean) {
				// 	const cleanFunction = await extension.configuratorClean.get(this, updateWithCurrentProductEvent);
				// 	await cleanFunction(currentInstance);
				// }
			} else {
				await replicatorComponent.component_disable();
				if (extension.presets && extension.presets.length) {
					hasPresets = true;
					const startEv = this.component_getEventWhichRepresentNoEvent();
					for (const preset of extension.presets) {
						// preset.
						const presetComponent = <ProductOptionTileNS.Component>await editOptionsSelectComponent.component_instantiateOuterChild<ProductOptionTileNS.Configuration>(
							{
								type: 'product-optionTile',
								id: preset.id,
								tree: editOptionsSelectComponent.component_tree,
								slot: null,
								isSlot: false,
								configuration: {
									option :  currentInstance.resolvePresetToBasketOption(preset),
									'@translations': {
										'title-attr': preset.name
									}
								}
							});
						// await presetComponent.component_instantiateOuterChild({
						// 	type: 'image',
						// 	id: 'image',
						// 	tree: presetComponent.component_tree,
						// 	slot: null,
						// 	isSlot: false,
						// 	configuration: {
						// 		URL: preset.iconURL,
						// 		'@translations': {
						// 			name: {
						// 				en: 'icon',
						// 				fr: 'icon',
						// 			}
						// 		},
						// 		'@classList': ['imgConstrainedByTheContainer']
						// 	}
						// });
						await presetComponent.component_initialize();
						await presetComponent.component_start(startEv);
						if (preset.id === currentInstance.currentPreset) {
							editOptionsSelectComponent.value = presetComponent;
							editOptionsSelectComponent.refreshFromCurrentValue();
						}
					}
					await editOptionsComponent.component_enable();
				}
				if (extension.customImages && (extension.customImages.length > 0)) {
					hasCustomImages = true;
				}
			}
		} else {
			await replicatorComponent.component_disable();
		}
		await this.updateWithCurrentProductPositionConstraints();
		await this.updateWithCurrentProductPosition();
		// =========================================================================
		// =========================================================================
		// =========================================================================
		// =========================================================================
		// =========================================================================
		editTitleComponent.component_DOM.dataset.translation = currentInstance.getNameTranslationId();
		editTitleComponent.component_DOM.dataset.translationAttr = 'title:' + currentInstance.getNameTranslationId();
		let enabledButtonsCount = 0;
		for (const buttonId of Object.keys(this.component_configuration.buttons)) {
			const button = <CM.DOMComponent<any, any, any>>this.component_find('@:main.' + buttonId);
			if (extension && extension.disabledButtons && extension.disabledButtons.includes(buttonId)) {
				await button.component_disable();
			} else {
				if (currentInstance.product.category.menuActions[<'delete' | 'edit' | 'rotate' | 'duplicate'>buttonId]) {
					if (button.component_activation.preventedByConfiguration) {
						await button.component_enable();
					}
				} else {
					if (!button.component_activation.preventedByConfiguration) {
						await button.component_disable();
					}
				}
				enabledButtonsCount++;
			}
		}
		if (!hasDescription) {
			delete editDescriptionComponent.component_DOM.dataset.translation;
			editDescriptionComponent.component_DOM.textContent = '';
		}
		if (!hasPresets) {
			await editOptionsComponent.component_disable();
		}
		if (!hasCustomImages) {
			await this.disableCustomImages();
		} else {
			await this.enableCustomImages();
		}
		STD.translationsManager.update(this.component_DOM);
		const editButton = <CM.DOMComponent<any, any, any>>this.component_find('@:main.edit');

		if (this.component_configuration.mode === 'contextual') {
			if ((enabledButtonsCount === 1) && !editButton.component_activation.preventedByConfiguration) {
				this.component_DOM.classList.add('onlyEdit');
				await this.changeBlock('edit');
			} else {
				this.component_DOM.classList.remove('onlyEdit');
				await this.changeBlock('main');
			}
		} else {
			await this.changeBlock('edit');
		}


		const lockButton = <InputButton.Component>this.component_find('@:edit.content.lock');
		const unlockButton = <InputButton.Component>this.component_find('@:edit.content.unlock');

		if (currentInstance.isLocked) {
			await lockButton.component_disable();
			await unlockButton.component_enable();
		} else {
			await lockButton.component_enable();
			await unlockButton.component_disable();
		}

		this.updatePosition();
	};

	async updateWithCurrentProductPosition() {
		const currentInstance = app.viewer.currentSelectedInstance;
		if (!currentInstance) {
			this.component_throwUserError('No instance currently selected!', {});
		}

		await (<InputNumber.Component>this.component_find('@:edit.content.position.y.first-line.input')).updateValue(currentInstance.position.scene.y * 100/** Meters to centimeters. **/);

		await (<InputNumber.Component>this.component_find('@:edit.content.rotation.x.first-line.input')).updateValue(currentInstance.position.scene.rotation.x);

		await (<InputNumber.Component>this.component_find('@:edit.content.rotation.y.first-line.input')).updateValue(currentInstance.position.scene.rotation.y);

		await (<InputNumber.Component>this.component_find('@:edit.content.rotation.z.first-line.input')).updateValue(currentInstance.position.scene.rotation.z);
	}

	async updateWithCurrentProductPositionConstraints() {
		const currentInstance = app.viewer.currentSelectedInstance;
		if (!currentInstance) {
			this.component_throwUserError('No instance currently selected!', {});
		}

		const paths = [
			'@:edit.content.position.y',
			'@:edit.content.rotation.x',
			'@:edit.content.rotation.y',
			'@:edit.content.rotation.z'
		];

		for (const path of paths) {
			for (const subPath of ['first-line.input', 'slider']) {
				await (<InputNumber.Component>this.component_find(path + '.' + subPath)).updateConstraints();
			}
		}
	}

	private isMenuClosingEnabled = true;

	async executeFunctionInNoMenuClosingBlock(fn: () => void | Promise<void>) {
		this.isMenuClosingEnabled = false;
		await fn();
		this.isMenuClosingEnabled = true;
	};

	getCustomImagesDescriptorFromGraphics(graphics: ViewerGraphicsComponent) {
		if (!graphics.component_parent) {
			this.component_throwUserError('Graphics do not have parent!', {
				graphics
			});
		}
		const idx = graphics.component_parent.component_outerChilds.indexOf(graphics);
		const currentInstance = this.getCurrentInstance();
		const extension = currentInstance.product.extension;
		if (!extension || !extension.customImages) {
			this.component_throwLibraryError('Current instance do not have customImage', {
				currentInstance
			});
		}
		const desc = extension.customImages[idx];
		if (!desc) {
			this.component_throwLibraryError('Custom image not found!', {
				currentInstance
			});
		}
		return desc;
	};

	getNodeLocalId(component: CM.Component<any, any>) {
		const ci = this.getCurrentInstance();
		let localId = ci.localId;
		if (component instanceof ViewerGraphicsComponent) {
			const idx = parseInt(component.id.split('-').slice(-1)[0] || '');
			if (ci.product.extension && ci.product.extension.customImages) {
				const customImageExtension = ci.product.extension.customImages[idx];
				if (customImageExtension && customImageExtension.getLocalId) {
					localId = customImageExtension.getLocalId.call(ci, customImageExtension.id);
				}
			}
		}
		return (localId);
	};

	getGraphicsSizeX(graphics: ViewerGraphicsComponent) {
		const desc = this.getCustomImagesDescriptorFromGraphics(graphics);
		if (!(graphics instanceof ViewerGraphicsComponent)) {
			this.component_throwUserError('', {});
		}
		const ci = this.getCurrentInstance();
		const idx = parseInt(graphics.id.split('-').slice(-1)[0] || '');
		if (ci.product.extension && ci.product.extension.customImages) {
			const customImageExtension = ci.product.extension.customImages[idx];
			if (customImageExtension && customImageExtension.getSizeX) {
				return customImageExtension.getSizeX.call(ci, customImageExtension.id);
			}
		}
		return desc.width;
	};

	getGraphicsSizeY(graphics: ViewerGraphicsComponent) {
		const desc = this.getCustomImagesDescriptorFromGraphics(graphics);
		if (!(graphics instanceof ViewerGraphicsComponent)) {
			this.component_throwUserError('', {});
		}
		const ci = this.getCurrentInstance();
		const idx = parseInt(graphics.id.split('-').slice(-1)[0] || '');
		if (ci.product.extension && ci.product.extension.customImages) {
			const customImageExtension = ci.product.extension.customImages[idx];
			if (customImageExtension && customImageExtension.getSizeY) {
				return customImageExtension.getSizeY.call(ci, customImageExtension.id);
			}
		}
		return (desc.height);
	};

	getGraphicsProductInstance() {
		return this.getCurrentInstance();
	};

	getMaterial(graphics: ViewerGraphicsComponent) {
		const desc = this.getCustomImagesDescriptorFromGraphics(graphics);
		return (desc.material);
	};

	getMesh(graphics: ViewerGraphicsComponent) {
		const desc = this.getCustomImagesDescriptorFromGraphics(graphics);
		return (desc.mesh);
	};

	private getCurrentInstance(): ViewerComponent.ProductInstance {
		const currentInstance = app.viewer.currentSelectedInstance;
		if (!currentInstance) {
			return <ViewerComponent.ProductInstance>this.previousInstance;
			// this.component_throwUserError('No instance currently selected!');
		}
		this.previousInstance = currentInstance;
		return (currentInstance);
	};

	updatePosition(lvl = 0) {
		if (this.component_configuration.mode !== 'contextual') {
			return;
		}
		let currentInstance: ViewerComponent.ProductInstance;
		try {
			currentInstance = this.getCurrentInstance();
		} catch (e) {
			console.warn(e);
			return;
		}
		const p = {
			x: currentInstance.position.UI.x,
			y: currentInstance.position.UI.y
		};
		if (!p.x || !p.y) {
			if (lvl < 5) {
				requestAnimationFrame(this.updatePosition.bind(this, lvl + 1));
				return;
			}
		}
		if (!this.component_DOM.parentElement) {
			this.component_throwLibraryError('', {});
		}
		const op = STD.DOM.getOffset(this.component_DOM.parentElement);
		const o = {
			x: op.left,
			y: op.top
		};
		const s = {
			x: this.component_DOM.offsetWidth,
			y: this.component_DOM.offsetHeight
		};
		const max = {
			x: CM.Manager.root.component_DOM.offsetWidth - 12,
			y: CM.Manager.root.component_DOM.offsetHeight - 12
		};
		if ((o.x + p.x + s.x) > max.x) {
			p.x = max.x - (o.x + s.x);
		}
		if ((o.y + p.y + s.y) > max.y) {
			p.y = max.y - (o.y + s.y);
		}
		p.x = Math.round(p.x);
		p.y = Math.round(p.y);
		if (p.x < 10) {
			p.x = 10;
		}
		if (p.y < 10) {
			p.y = 10;
		}
		this.component_stylize({
			left: p.x + 'px',
			top: p.y + 'px'
		}, 'self');
	};

	async changeBlock(blockId: CM.Component.TId) { //eslint-disable-line require-await
		const childs = <CM.DOMComponent<any, any, any>[]>[
			...this.component_innerChilds,
			...this.component_outerChilds
		];
		const block = childs.find(x => x.id === blockId);
		if (!block) {
			this.component_throwUserError('Block not found', {
				blockId
			});
		}
		block.component_stylize({display: ''}, 'self');
		block.component_stylize({display: 'none'}, 'others');
		for (const el of block.component_DOM.querySelectorAll('.notHighlighted')) {
			el.classList.remove('notHighlighted');
		}
		for (const el of block.component_DOM.querySelectorAll('.highlighted')) {
			el.classList.remove('highlighted');
		}
		this.updatePosition();
	};

	async onButtonClick(ev: STD.events.Event<any, CM.Component<any, any>>) {
		const bId = <string>ev.target.id;
		const bConf = this.component_configuration.buttons[bId];
		if (!bConf) {
			this.component_throwUserError(
				'Button configuration not found!',
				{buttonId: bId}
			);
		}
		const action = (bConf.action === 'default') ? bId : bConf.action;
		if (action.startsWith('custom-')) {
			throw 'NOT IMPLEMENTED!';
		} else {
			const method = (<any>this)['action_' + action];
			await method.call(this);
		}
	};

	async onOptionSelected(presetId: string) {
		const currentInstance = app.viewer.currentSelectedInstance;
		if (!currentInstance) {
			this.component_throwUserError('No instance currently selected!', {});
		}
		await currentInstance.usePreset(presetId);
	};

	async unselectAndClose() {
		if (this.component_parent.currentSelectedInstance) {
			await this.component_parent.currentSelectedInstance.unselect();
			await this.component_disable();
		}
	};

	// position_y;
	position_y_getMin() {
		// const CI = this.getCurrentInstance();
		// scene
		// product in viewer
		// component in app
		// return CI.position.scene.y;
		return 0;
	};

	position_y_getMax() {
		return 300;
	};

	position_y_onChange(event: STD.events.Event<any, any>) {
		const CI = this.getCurrentInstance();
		if (!CI) {
			return;
		}
		CI.updatePosition({
			y: event.details.newValue / 100
		});
	};

	rotation_onChange(event: STD.events.Event<any, any>, axis: 'x' | 'y' | 'z') {
		const CI = this.getCurrentInstance();
		if (!CI) {
			return;
		}
		CI.updatePosition({
			rotation: {
				[axis]: event.details.newValue
			}
		});
	};

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: IViewerMenuComponentConfiguration
	) {
		super(descriptor, configuration);
		//Remove unused errors for theses private dynamically called method;
		void this.action_edit;
		void this.action_rotate;
		void this.action_duplicate;
		void this.action_delete;
		void this.action_toggle_lock;

		if (!this.component_parent || !(this.component_parent instanceof ViewerComponent)) {
			this.component_throwUserError(`Component<${this.component_getPath()}> must be a child of a viewer!`, {});
		}
		this.component_parent.menu = this;
		const plannerComponent = <PlannerComponent>this.component_parent.component_outerChilds.find(
			x => x instanceof PlannerComponent
		);
		if (!plannerComponent) {
			this.component_throwUserError(`Component<${this.component_getPath()}> must be a sibling of a planner component!`, {});
		}
		this.component_DOM = <HTMLDivElement>STD.DOM.factory({
			dataset: {
				mode: this.component_configuration.mode
			}
		});
		this.component_parent.addEventListener(
			'selection',
			STD.misc.createDebouncer({
				fn: <any>this.onProductInstanceSelection.bind(this),
				delay: 25
			}),
			{subType: '*', listener: this}
		);
		this.component_parent.addEventListener(
			'message',
			this.onCameraUpdated.bind(this),
			{subType: 'onEndCameraInteraction', listener: this}
		);
		this.component_parent.addEventListener(
			'message',
			this.component_disableIfIsEnabled.bind(this),
			{subType: 'onProductNodeInstanceDraggingStarted', listener: this}
		);
		this.component_DOM.addEventListener('click', ev => ev.stopPropagation());
		const ro = new ResizeObserver(() => {
			if (this.component_isDisabled || !this.component_isStarted) {
				return;
			}
			this.updatePosition();
		});
		ro.observe(this.component_DOM);
	};
};
export namespace NS {
	export const Component = ViewerMenuComponent;
	export type Component = ViewerMenuComponent;
	export type Configuration = IViewerMenuComponentConfiguration;
}
import manifestXMLSource from './ViewerMenu.xml';
import './ViewerMenu.scss';
import ViewerComponent             from '../Viewer/Viewer';
import { NS as ProductOptionTileNS }              from '../../product/ProductOptionTile/ProductOptionTile';
import { PlannerComponent }        from '../planner/Planner/ViewerPlanner';
import { ViewerGraphicsComponent } from '../Graphics/ViewerGraphics';

CM.Manager.declare({
	id: 'viewer-menu',
	type: ViewerMenuComponent,
	manifest: manifestXMLSource
});
